import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Campaign, AudienceFilter } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export function useCampaigns() {
  return useQuery<Campaign[]>({
    queryKey: ['campaigns'],
    queryFn: async (): Promise<Campaign[]> => {
      const { data, error } = await db
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Campaign[];
    },
    staleTime: 30_000,
  });
}

export function useCampaign(id: string | null) {
  return useQuery<Campaign | null>({
    queryKey: ['campaign', id],
    queryFn: async (): Promise<Campaign | null> => {
      if (!id) return null;
      const { data, error } = await db
        .from('campaigns')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as Campaign;
    },
    enabled: !!id,
  });
}

/** Prévia de audiência — conta contatos que satisfazem o filtro */
export function useAudiencePreview(filter: AudienceFilter) {
  return useQuery<number>({
    queryKey: ['audience-preview', filter],
    queryFn: async (): Promise<number> => {
      let query = supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true });

      if (filter.activeOnly !== false) {
        query = query.eq('active', true);
      }
      if (filter.categories && filter.categories.length > 0) {
        query = query.in('category', filter.categories);
      }
      if (filter.neighborhood) {
        query = query.ilike('neighborhood', `%${filter.neighborhood}%`);
      }
      // tags: filtro AND (contato deve ter todas as tags)
      if (filter.tags && filter.tags.length > 0) {
        query = query.contains('tags', filter.tags);
      }

      const { count, error } = await query;
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 10_000,
  });
}

/** Stats agregadas para o dashboard de campanhas */
export interface CampaignStats {
  total: number;
  draft: number;
  running: number;
  completed: number;
  deliveryRate: number; // % média de entregas vs enviados
}

export function useCampaignStats() {
  return useQuery<CampaignStats>({
    queryKey: ['campaigns', 'stats'],
    queryFn: async (): Promise<CampaignStats> => {
      const { data, error } = await db
        .from('campaigns')
        .select('status, stats_sent, stats_delivered');
      if (error) throw error;

      const all = (data ?? []) as Pick<Campaign, 'status' | 'stats_sent' | 'stats_delivered'>[];
      const totalSent = all.reduce((s, c) => s + (c.stats_sent ?? 0), 0);
      const totalDelivered = all.reduce((s, c) => s + (c.stats_delivered ?? 0), 0);

      return {
        total:        all.length,
        draft:        all.filter((c) => c.status === 'draft').length,
        running:      all.filter((c) => c.status === 'running').length,
        completed:    all.filter((c) => c.status === 'completed').length,
        deliveryRate: totalSent > 0 ? Math.round((totalDelivered / totalSent) * 100) : 0,
      };
    },
    staleTime: 30_000,
  });
}

// ── Templates WhatsApp aprovados do tenant atual ──────────────────────────────
// RLS garante isolamento por tenant — não é necessário passar tenant_id.

export interface ApprovedTemplate {
  id:            string;
  name:          string;
  language:      string;
  body:          string;
  use_case:      string;
  button_labels: string[];
}

export function useApprovedWhatsAppTemplates() {
  return useQuery<ApprovedTemplate[]>({
    queryKey: ['approved-wa-templates'],
    queryFn: async () => {
      const { data, error } = await db
        .from('whatsapp_templates')
        .select('id, name, language, body, use_case, button_labels')
        .eq('status', 'approved')
        .order('name');
      if (error) throw error;
      return (data ?? []) as ApprovedTemplate[];
    },
    staleTime: 60_000,
  });
}
